// src/Routes.jsx
import { Suspense, lazy } from 'react';
import {
  BrowserRouter,
  Routes as RouterRoutes,
  Route,
  Navigate,
  useLocation,
} from 'react-router-dom';
import ErrorBoundary from './components/ErrorBoundary';
import ScrollToTop from './components/ScrollToTop';
import DashboardLayout from './components/DashboardLayout';
import { useAuth } from './contexts/AuthContext';
import { hasRole as hasRequiredRole } from './constants/roles';

// Eager-load critical pages for better initial performance
import NotFound from './pages/NotFound';
import AccessDenied from './pages/AccessDenied';
import DataMissing from './pages/DataMissing';
import LandingPage from './pages/landing-page';
import EmployeeLoginPortal from './pages/employee-login-portal';
import AuthReset from './auth/Reset.jsx';
import ChatWidget from './components/AIAssistant/ChatWidget';

// Lazy-load all other pages to reduce initial bundle size
const EmployeeAttendanceDashboard = lazy(
  () => import('./pages/employee-attendance-dashboard')
);
const AdministratorEmployeeManagementConsole = lazy(
  () => import('./pages/administrator-employee-management-console')
);
const AttendanceHistoryAndAnalyticsDashboard = lazy(
  () => import('./pages/attendance-history-and-analytics-dashboard')
);
const NominaPage = lazy(() => import('./pages/nomina'));
const ConstructionSiteAndSupervisorManagementHub = lazy(
  () => import('./pages/construction-site-and-supervisor-management-hub')
);
const IncidentRegistrationAndManagementSystem = lazy(
  () => import('./pages/incident-registration-and-management-system')
);
const ComprehensiveReportingAndExportCenter = lazy(
  () => import('./pages/comprehensive-reporting-and-export-center')
);
const SystemAdministrationAndConfigurationPanel = lazy(
  () => import('./pages/system-administration-and-configuration-panel')
);
const UserProfileManagementAndAuthenticationCenter = lazy(
  () => import('./pages/user-profile-management-and-authentication-center')
);
const RoleBasedAccessControlManagementSystem = lazy(
  () => import('./pages/role-based-access-control-management-system')
);
const ActivityLoggingAndSecurityMonitoringDashboard = lazy(
  () => import('./pages/activity-logging-and-security-monitoring-dashboard')
);
const ObrasFinancialControlManagement = lazy(
  () => import('./pages/obras-financial-control-management')
);
const FinanceDashboard = lazy(() => import('./pages/FinanceDashboard'));
const SiteFinancialManagement = lazy(
  () => import('./pages/site-financial-management')
);
const SupervisorPortal = lazy(() => import('./pages/supervisor-portal'));
const CatalogosPage = lazy(() => import('./pages/catalogos'));
const AdminSyncControl = lazy(() => import('./pages/admin-sync-control'));
const ComprasManagementPage = lazy(() => import('./pages/compras-management'));
const InventarioManagementPage = lazy(
  () => import('./pages/inventario-management')
);
const CajaManagementPage = lazy(() => import('./pages/caja-management'));
const ChequesAgendaPage = lazy(() => import('./pages/cheques-agenda'));
const ClienteDetailPage = lazy(() => import('./pages/cliente-detail'));
const ProveedorDetailPage = lazy(() => import('./pages/proveedor-detail'));
const ObraDetailPage = lazy(() => import('./pages/obra-detail'));
const HelpCenter = lazy(() => import('./pages/help-center'));
const MiPortalPage = lazy(() => import('./pages/mi-portal'));
const VacationsManagementPage = lazy(() =>
  import('./pages/leave-management').then(module => ({
    default: module.VacationsManagementPage,
  }))
);
const PermissionsManagementPage = lazy(() =>
  import('./pages/leave-management').then(module => ({
    default: module.PermissionsManagementPage,
  }))
);

// --- ProtectedRoute con soporte de `next` y DashboardLayout ---
function ProtectedRoute({ children, requiredRole = null }) {
  const { user, userProfile, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center space-y-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="text-gray-600">Cargando...</span>
        </div>
      </div>
    );
  }

  if (!user) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }

  if (requiredRole) {
    const userRole = userProfile?.role;

    if (!hasRequiredRole(userRole || '', requiredRole)) {
      switch (userRole) {
        case 'superadmin':
          return <Navigate to="/admin/system" replace />;
        case 'admin':
          return <Navigate to="/admin/employees" replace />;
        case 'supervisor':
          return <Navigate to="/supervisor/sites" replace />;
        default:
          return <Navigate to="/dashboard" replace />;
      }
    }
  }

  return <DashboardLayout>{children}</DashboardLayout>;
}

// Loading fallback component with better UX
const LoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="flex flex-col items-center space-y-4">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      <span className="text-muted-foreground">Cargando página...</span>
    </div>
  </div>
);

// Main routing component
function AppRoutes() {
  return (
    <Suspense fallback={<LoadingFallback />}>
      <RouterRoutes>
        {/* Public Routes */}
        <Route path="/login" element={<EmployeeLoginPortal />} />
        <Route
          path="/employee-login-portal"
          element={<EmployeeLoginPortal />}
        />

        {/* Password reset callback (Supabase) */}
        <Route path="/auth/reset" element={<AuthReset />} />

        {/* Profile Management - Accessible to all authenticated users */}
        <Route
          path="/profile-center"
          element={
            <ProtectedRoute>
              <UserProfileManagementAndAuthenticationCenter />
            </ProtectedRoute>
          }
        />

        {/* Help Center - available to every authenticated role */}
        <Route
          path="/help"
          element={
            <ProtectedRoute>
              <HelpCenter />
            </ProtectedRoute>
          }
        />

        {/* Employee Routes */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <EmployeeAttendanceDashboard />
            </ProtectedRoute>
          }
        />

        <Route
          path="/mi-portal"
          element={
            <ProtectedRoute>
              <MiPortalPage />
            </ProtectedRoute>
          }
        />

        {/* Backwards compatibility: consolidated into canonical employee dashboard */}
        <Route
          path="/personalized-worker-dashboard-with-site-integration-and-team-collaboration"
          element={<Navigate to="/dashboard" replace />}
        />

        {/* Supervisor Routes */}
        <Route
          path="/supervisor/sites"
          element={
            <ProtectedRoute requiredRole="supervisor">
              <ConstructionSiteAndSupervisorManagementHub />
            </ProtectedRoute>
          }
        />
        <Route
          path="/supervisor/portal"
          element={
            <ProtectedRoute requiredRole="supervisor">
              <SupervisorPortal />
            </ProtectedRoute>
          }
        />

        {/* Administrator Routes */}
        <Route
          path="/admin/sites"
          element={
            <ProtectedRoute requiredRole="admin">
              <ConstructionSiteAndSupervisorManagementHub />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/employees"
          element={
            <ProtectedRoute requiredRole="admin">
              <AdministratorEmployeeManagementConsole />
            </ProtectedRoute>
          }
        />

        {/* Backwards compatibility: consolidated into canonical employee administration */}
        <Route
          path="/enhanced-employee-management-console-with-deletion-controls"
          element={<Navigate to="/admin/employees" replace />}
        />
        {/* Backwards compatibility: legacy RBAC surface consolidated into /admin/roles */}
        <Route
          path="/role-based-permission-enforcement-and-security-management-system"
          element={<Navigate to="/admin/roles" replace />}
        />

        {/* Obras Financial Control Management */}
        <Route
          path="/admin/obras-financiero"
          element={
            <ProtectedRoute requiredRole="admin">
              <ObrasFinancialControlManagement />
            </ProtectedRoute>
          }
        />

        {/* Catalogos - Companies & Dependencies Management */}
        <Route
          path="/admin/catalogos"
          element={
            <ProtectedRoute requiredRole="admin">
              <CatalogosPage />
            </ProtectedRoute>
          }
        />

        {/* Compras Management */}
        <Route
          path="/admin/compras"
          element={
            <ProtectedRoute requiredRole="admin">
              <ComprasManagementPage />
            </ProtectedRoute>
          }
        />

        {/* Inventario Management */}
        <Route
          path="/admin/inventario"
          element={
            <ProtectedRoute requiredRole="admin">
              <InventarioManagementPage />
            </ProtectedRoute>
          }
        />

        {/* Caja General Management */}
        <Route
          path="/admin/caja"
          element={
            <ProtectedRoute requiredRole="admin">
              <CajaManagementPage />
            </ProtectedRoute>
          }
        />

        {/* Agenda de Cheques (Check Plus) */}
        <Route
          path="/admin/tesoreria/agenda-cheques"
          element={
            <ProtectedRoute requiredRole="admin">
              <ChequesAgendaPage />
            </ProtectedRoute>
          }
        />

        {/* Backwards compatibility: overtime is calculated inside payroll, like Nova-Taller */}
        <Route
          path="/admin/tiempo-extra"
          element={<Navigate to="/admin/attendance" replace />}
        />

        {/* Finance Dashboard - Per-Site Financial KPIs */}
        <Route
          path="/finanzas"
          element={
            <ProtectedRoute requiredRole="admin">
              <FinanceDashboard />
            </ProtectedRoute>
          }
        />

        {/* Site Financial Management - Expenses, Payments, Invoices */}
        <Route
          path="/admin/sites/:siteId/financial"
          element={
            <ProtectedRoute requiredRole="admin">
              <SiteFinancialManagement />
            </ProtectedRoute>
          }
        />

        {/* Admin */}
        <Route
          path="/admin/attendance"
          element={
            <ProtectedRoute requiredRole="admin">
              <AttendanceHistoryAndAnalyticsDashboard />
            </ProtectedRoute>
          }
        />
        {/* Nómina unificada: tabs Semanal / Aguinaldos / Finiquitos */}
        <Route
          path="/admin/nomina"
          element={
            <ProtectedRoute requiredRole="admin">
              <NominaPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/vacaciones"
          element={
            <ProtectedRoute requiredRole="admin">
              <VacationsManagementPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/permisos"
          element={
            <ProtectedRoute requiredRole="admin">
              <PermissionsManagementPage />
            </ProtectedRoute>
          }
        />
        {/* Backwards-compat: /admin/payroll redirige al motor unificado */}
        <Route
          path="/admin/payroll"
          element={<Navigate to="/admin/nomina" replace />}
        />
        <Route
          path="/admin/incidents"
          element={
            <ProtectedRoute requiredRole="admin">
              <IncidentRegistrationAndManagementSystem />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/reports"
          element={
            <ProtectedRoute requiredRole="admin">
              <ComprehensiveReportingAndExportCenter />
            </ProtectedRoute>
          }
        />

        {/* SuperAdmin */}
        <Route
          path="/admin/system"
          element={
            <ProtectedRoute requiredRole="superadmin">
              <SystemAdministrationAndConfigurationPanel />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/roles"
          element={
            <ProtectedRoute requiredRole="admin">
              <RoleBasedAccessControlManagementSystem />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/sync"
          element={
            <ProtectedRoute requiredRole="superadmin">
              <AdminSyncControl />
            </ProtectedRoute>
          }
        />
        <Route
          path="/activity-logging-and-security-monitoring-dashboard"
          element={
            <ProtectedRoute requiredRole="admin">
              <ActivityLoggingAndSecurityMonitoringDashboard />
            </ProtectedRoute>
          }
        />

        {/* Cliente Detail */}
        <Route
          path="/clientes/:id"
          element={
            <ProtectedRoute requiredRole="admin">
              <ClienteDetailPage />
            </ProtectedRoute>
          }
        />

        {/* Proveedor Detail */}
        <Route
          path="/proveedores/:id"
          element={
            <ProtectedRoute requiredRole="admin">
              <ProveedorDetailPage />
            </ProtectedRoute>
          }
        />

        {/* Obra Detail */}
        <Route
          path="/obras/:id"
          element={
            <ProtectedRoute requiredRole="admin">
              <ObraDetailPage />
            </ProtectedRoute>
          }
        />

        {/* Landing Page */}
        <Route path="/" element={<LandingPage />} />

        {/* Error Pages */}
        <Route path="/403" element={<AccessDenied />} />
        <Route path="/data-missing" element={<DataMissing />} />

        {/* 404 */}
        <Route path="*" element={<NotFound />} />
      </RouterRoutes>
    </Suspense>
  );
}

export default function Routes() {
  return (
    <BrowserRouter
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true,
      }}
    >
      <ErrorBoundary>
        <div className="flex flex-col min-h-screen">
          <ScrollToTop />
          <div className="flex-grow">
            <AppRoutes />
          </div>
          {/* ChatWidget se monta DENTRO del BrowserRouter para que useLocation()
              funcione y se oculte automáticamente en rutas públicas. */}
          <ChatWidget />
        </div>
      </ErrorBoundary>
    </BrowserRouter>
  );
}
